import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { History, Search } from 'lucide-react';
import { useUi } from '@/store/ui';
import { useAuth } from '@/store/auth';
import { useCan, useTerms } from '@/hooks/usePermission';
import { useDebounce } from '@/hooks/useDebounce';
import { readRecent } from '@/hooks/useRecent';
import { searchApi } from '@/services/admin';
import { cn } from '@/lib/cn';
import { Modal } from '@/components/ui';
import { buildNav, flattenNav, visibleNav } from './nav';

const TYPE_LABEL: Record<string, string> = { lead: 'Lead', client: 'Client', task: 'Task', employee: 'Employee', page: 'Go to' };

export function CommandPalette() {
  const { commandOpen, setCommandOpen } = useUi();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const debounced = useDebounce(q.trim(), 250);
  const navigate = useNavigate();
  const can = useCan();
  const terms = useTerms();
  const tracks = useAuth((s) => !!s.user?.tracksWorkTime);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setCommandOpen]);

  useEffect(() => {
    if (commandOpen) {
      setQ('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [commandOpen]);

  const search = useQuery({ queryKey: ['search', debounced], queryFn: () => searchApi.search(debounced), enabled: commandOpen && debounced.length >= 2 });
  const pages = useMemo(
    () =>
      flattenNav(visibleNav(buildNav(terms), can, tracks))
        .filter((it, idx, all) => all.findIndex((x) => x.to === it.to) === idx)
        .map((it) => ({ type: 'page', id: it.to, title: it.label, subtitle: undefined as string | undefined, link: it.to })),
    [terms, can, tracks],
  );
  const lower = q.trim().toLowerCase();
  const results = lower
    ? [...pages.filter((p) => p.title.toLowerCase().includes(lower)), ...(search.data?.results ?? [])]
    : readRecent().map((r) => ({ type: r.type, id: r.link, title: r.title, subtitle: 'Recently viewed', link: r.link }));

  const go = (link: string) => {
    setCommandOpen(false);
    navigate(link);
  };

  return (
    <Modal open={commandOpen} onClose={() => setCommandOpen(false)} title="Search" size="md">
      <div className="flex items-center gap-2 rounded-lg border border-line px-3 focus-within:border-brand">
        <Search className="h-4 w-4 text-ink-subtle" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === 'Enter' && results[active]) go(results[active]!.link);
          }}
          placeholder="Type to search records and pages…"
          className="h-11 w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-subtle"
          aria-label="Search"
        />
      </div>
      <ul className="mt-3 max-h-80 overflow-y-auto" role="listbox">
        {!lower && results.length === 0 && <li className="py-6 text-center text-sm text-ink-subtle">Start typing to search. Results only include records you are allowed to see.</li>}
        {lower && debounced.length >= 2 && search.isFetching && <li className="py-2 text-center text-xs text-ink-subtle">Searching…</li>}
        {lower && !search.isFetching && results.length === 0 && debounced.length >= 2 && <li className="py-6 text-center text-sm text-ink-subtle">No matches for “{q}”.</li>}
        {results.map((r, idx) => (
          <li key={`${r.type}-${r.id}`} role="option" aria-selected={idx === active}>
            <button type="button" onMouseEnter={() => setActive(idx)} onClick={() => go(r.link)} className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left', idx === active ? 'bg-brand-soft' : 'hover:bg-surface-hover')}>
              {!lower ? <History className="h-4 w-4 text-ink-subtle" /> : <span className="w-16 shrink-0 text-[11px] font-medium uppercase tracking-wide text-ink-subtle">{TYPE_LABEL[r.type] ?? r.type}</span>}
              <span className="min-w-0">
                <span className="block truncate text-sm text-ink">{r.title}</span>
                {r.subtitle && <span className="block truncate text-xs text-ink-subtle">{r.subtitle}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
